import React, { useState, useEffect, useRef } from "react";
import styles from "./challengedetails.module.css";
import mockData from "./mockData";
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io";
import ShareChallengeDialog from "../../Components/Share/ShareChallengeDialog";
import LoadingOverlay from "../../Components/Dialog/LoadingOverlay";
import { open_api_with_cookies, getCountryCode } from "../../Utils/network";
import { useParams, useNavigate } from "react-router-dom";
import PageNotFound from "../PageNotFound/PageNotFound";
import ChallengePaymentPage from "./ChallengePaymentPage";
// Reusable scroll animation hook
const useScrollAnimation = (ref, threshold = 0.2, rootMargin = "0px") => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);

            // Find all child elements with animation classes and add visible class
            const animatedChildren = entry.target.querySelectorAll(
              `.${styles.fadeOnScroll}, .${styles.slideUpOnScroll}, .${styles.slideLeftOnScroll}, .${styles.slideRightOnScroll}, .${styles.animateOnScroll}`
            );

            animatedChildren.forEach((el, index) => {
              // Skip elements inside the FAQ answer to prevent animation conflicts
              if (el.closest(`.${styles.faqAnswer}`)) return;

              // Add staggered delay based on index
              el.style.transitionDelay = `${0.1 * index}s`;
              el.classList.add(styles.visible);
            });

            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [ref, threshold, rootMargin]);
};

// Section Navigator Component
const SectionNavigator = ({ sections, activeSection, onSectionClick }) => {
  return (
    <nav className={styles.sectionNavigator} aria-label="Section navigation">
      <ul className={styles.sectionNavigatorList}>
        {sections.map((section) => (
          <li key={section.id}>
            <button
              className={`${styles.sectionNavButton} ${activeSection === section.id ? styles.activeSection : ""}`}
              onClick={() => onSectionClick(section.id)}
              aria-current={activeSection === section.id ? "true" : "false"}
            >
              {section.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// Hero Section Component
const HeroSection = ({
  data,
  countryCode,
  onRegisterClick,
  price,
  isRegistrationDisabled,
}) => {
  // Initial animations that play when page loads (no intersection observer needed)
  console.log(data);
  return (
    <section id="hero" className={`${styles.heroSection} ${styles.fadeIn}`}>
      <h1>{data.hero_title || "Feel Amazing in Just 21 Days"}</h1>
      <p
        className={`${styles.heroSubtitle} ${styles.slideUp}`}
        dangerouslySetInnerHTML={{
          __html:
            data.hero_description ||
            "Turn low energy, stress, and scattered habits into a daily rhythm of movement, mindful eating, and calm",
        }}
      />
      <div className={styles.buttonContainer}>
        <button
          className={`${styles.registerButton} ${styles.pulseAnimation} ${isRegistrationDisabled ? styles.disabledButton : ""}`}
          onClick={onRegisterClick}
          aria-label={`Register now at ${countryCode === "in" ? "₹" : "$"}${price}`}
          disabled={isRegistrationDisabled}
        >
          <span className={styles.registerButtonText}>
            {isRegistrationDisabled
              ? "Registration closed"
              : `Register now at ${countryCode === "in" ? "₹" : "$"}${price}`}
          </span>
          <span className={styles.registerButtonIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="24"
              height="24"
              aria-hidden="true"
            >
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </span>
        </button>
      </div>
      <p className={`${styles.registeredCount}`}>
        {data.enrolments && data.enrolments > 0 ? (
          <span className={styles.countAnimation}>{data.enrolments}</span>
        ) : (
          <span className={styles.countAnimation}>{data.enrolments || 10}</span>
        )}
        + people have already registered
      </p>
    </section>
  );
};

// About Challenge Component
const AboutChallenge = ({ data }) => {
  const sectionRef = useRef(null);
  useScrollAnimation(sectionRef);

  const aboutImage = data.media.find(
    (item) => item.image_section === "about_challenge"
  );

  return (
    <section id="about" className={styles.aboutChallenge} ref={sectionRef}>
      <div className={`${styles.aboutWrapper} ${styles.animateOnScroll}`}>
        <div
          className={`${styles.aboutImageContainer} ${styles.slideRightOnScroll}`}
        >
          {aboutImage && (
            <img
              src={aboutImage.media}
              alt="About the challenge"
              loading="lazy"
            />
          )}
        </div>
        <div className={`${styles.aboutContent} ${styles.slideLeftOnScroll}`}>
          <h2>About the challenge</h2>
          <div
            className={styles.aboutText}
            dangerouslySetInnerHTML={{ __html: data.about_challenge }}
          />
        </div>
      </div>
    </section>
  );
};

// Benefits Component
const Benefits = ({ benefits = [] }) => {
  const sectionRef = useRef(null);
  useScrollAnimation(sectionRef, 0.1);

  // Grid layout classes to cycle through
  const gridClasses = [
    "energyCard", // Position 0 - Top left
    "sleepCard", // Position 1 - Top right
    "eatingCard", // Position 2 - Middle left
    "mindCard", // Position 3 - Middle right
    "strengthCard", // Position 4 - Bottom left
    "consistencyCard", // Position 5 - Bottom right
  ];

  // Get class based on index (cycle through available classes)
  const getBenefitClass = (index) => {
    // Use modulo to cycle through classes if more benefits than classes
    const classIndex = index % gridClasses.length;
    return styles[gridClasses[classIndex]] || "";
  };

  // Determine if image should be below text based on position
  // For this layout, we'll put images below for positions 1 and 4
  const isImageBelow = (index) => {
    return index % gridClasses.length === 1 || index % gridClasses.length === 4;
  };

  // Render a single benefit card
  const renderBenefitCard = (benefit, index) => {
    if (!benefit) return null;

    // Skip positions 2 and 3 if using the middle split row
    const posIndex = index % gridClasses.length;
    if (benefits.length >= 4 && (posIndex === 2 || posIndex === 3)) return null;

    const showImageBelow = isImageBelow(index);

    return (
      <div
        key={benefit.id || `benefit-${index}`}
        className={`${styles.bentoCard} ${getBenefitClass(index)}`}
      >
        <h3>{benefit.benefit_title}</h3>

        {!showImageBelow && (
          <div className={styles.bentoImage}>
            <img src={benefit.image} alt={benefit.benefit_title} />
          </div>
        )}

        <div
          className={styles.benefitDescription}
          dangerouslySetInnerHTML={{ __html: benefit.benefit_description }}
        />

        {showImageBelow && (
          <div className={styles.bentoImage}>
            <img src={benefit.image} alt={benefit.benefit_title} />
          </div>
        )}
      </div>
    );
  };

  // Handle the layout for the middle section (positions 2 and 3)
  const renderMiddleSection = () => {
    // Only render the split row if we have at least 4 benefits
    if (benefits.length < 4) return null;

    // Get benefits that would be in positions 2 and 3
    const leftBenefit = benefits[2];
    const rightBenefit = benefits[3];

    if (!leftBenefit || !rightBenefit) return null;

    return (
      <div className={styles.splitRow}>
        <div className={`${styles.bentoCard} ${styles.eatingCard}`}>
          <h3>{leftBenefit.benefit_title}</h3>
          <div className={styles.bentoImage}>
            <img src={leftBenefit.image} alt={leftBenefit.benefit_title} />
          </div>
          <div
            className={styles.benefitDescription}
            dangerouslySetInnerHTML={{
              __html: leftBenefit.benefit_description,
            }}
          />
        </div>

        <div className={`${styles.bentoCard} ${styles.mindCard}`}>
          <h3>{rightBenefit.benefit_title}</h3>
          <div className={styles.bentoImage}>
            <img src={rightBenefit.image} alt={rightBenefit.benefit_title} />
          </div>
          <div
            className={styles.benefitDescription}
            dangerouslySetInnerHTML={{
              __html: rightBenefit.benefit_description,
            }}
          />
        </div>
      </div>
    );
  };

  // Skip section if no benefits
  if (!benefits || benefits.length === 0) {
    return null;
  }

  return (
    <section id="benefits" className={styles.benefitsSection} ref={sectionRef}>
      <h2 className={`${styles.benefitsTitle} ${styles.fadeOnScroll}`}>
        How this helps you
      </h2>
      <div className={`${styles.bentoGrid} ${styles.slideUpOnScroll}`}>
        {/* Render benefits in order */}
        {benefits.map((benefit, index) => renderBenefitCard(benefit, index))}

        {/* Special middle section */}
        {renderMiddleSection()}
      </div>
    </section>
  );
};

// Date Range Component
const DateRange = ({ start_date, end_date }) => {
  const sectionRef = useRef(null);
  useScrollAnimation(sectionRef);

  // Format dates from batchData
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "2-digit",
    });
  };

  const startDate = start_date ? formatDate(start_date) : "";
  const endDate = end_date ? formatDate(end_date) : "";

  return (
    <div className={styles.dateRange} ref={sectionRef}>
      <div className={`${styles.dateRangeCard} ${styles.fadeOnScroll}`}>
        <div className={styles.dateBox}>
          <span>From:</span>
          <div className={styles.dateContent}>{startDate}</div>
        </div>
        <div className={styles.dateBox}>
          <span>To:</span>
          <div className={styles.dateContent}>{endDate}</div>
        </div>
      </div>
    </div>
  );
};

// Testimonials Component
const Testimonials = ({ testimonials = [] }) => {
  const sectionRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cardsPerView, setCardsPerView] = useState(3); // Default to 3 cards

  // Update cards per view based on screen size
  useEffect(() => {
    const updateCardsPerView = () => {
      if (window.innerWidth <= 767) {
        setCardsPerView(1); // Mobile: 1 card
      } else if (window.innerWidth <= 1100) {
        setCardsPerView(2); // Tablet: 2 cards
      } else {
        setCardsPerView(3); // Desktop: 3 cards
      }
    };

    // Initial call
    updateCardsPerView();

    // Add event listener for resize
    window.addEventListener("resize", updateCardsPerView);

    // Cleanup
    return () => {
      window.removeEventListener("resize", updateCardsPerView);
    };
  }, []);

  // Add scroll animation
  useScrollAnimation(sectionRef, 0.1);

  const handlePrevious = () => {
    setActiveIndex(Math.max(0, activeIndex - 1));
  };

  const handleNext = () => {
    setActiveIndex(
      Math.min(
        testimonials ? testimonials.length - cardsPerView : 0,
        activeIndex + 1
      )
    );
  };

  // Determine which testimonials to show
  const visibleTestimonials = testimonials
    ? testimonials.slice(activeIndex, activeIndex + cardsPerView)
    : [];

  // Determine button visibility
  const showPrevButton = activeIndex > 0;
  const showNextButton =
    testimonials && activeIndex < testimonials.length - cardsPerView;

  return (
    <section
      id="testimonials"
      className={styles.testimonialsSection}
      ref={sectionRef}
    >
      <h2 className={`${styles.fadeOnScroll}`}>What people are saying</h2>
      <div
        className={`${styles.testimonialsContainer} ${styles.slideUpOnScroll}`}
      >
        <div className={styles.carouselWrapper}>
          <button
            className={`${styles.carouselButton} ${styles.carouselButtonPrev} ${!showPrevButton ? styles.buttonHidden : ""}`}
            onClick={handlePrevious}
            aria-label="Previous testimonial"
            disabled={!showPrevButton}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <div
            className={styles.testimonialsGrid}
            style={{
              gridTemplateColumns: `repeat(${cardsPerView}, 1fr)`,
            }}
          >
            {visibleTestimonials &&
              visibleTestimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id || index}
                  className={styles.testimonialCard}
                >
                  <div className={styles.testimonialHeader}>
                    <div className={styles.profileIcon}>
                      {testimonial.profileImage ? (
                        <img
                          src={testimonial.profileImage}
                          alt={testimonial.name || "User"}
                        />
                      ) : (
                        <div className={styles.gradientIcon}></div>
                      )}
                    </div>
                    <div className={styles.authorInfo}>
                      <span className={styles.authorName}>
                        {testimonial.name || "Happy User"}
                      </span>
                      <span className={styles.authorDesignation}>
                        {testimonial.designation || "Challenge Participant"}
                      </span>
                    </div>
                  </div>

                  <div
                    className={styles.testimonialContent}
                    dangerouslySetInnerHTML={{
                      __html: testimonial.testimonial || "",
                    }}
                  />

                  {testimonial.challengeBadge && (
                    <div className={styles.challengeBadge}>
                      <div className={styles.badgeLabel}>
                        {testimonial.challengeBadge}
                      </div>
                      <div className={styles.beforeAfterImage}>
                        <img
                          src={testimonial.beforeAfterImage}
                          alt="Before and After"
                        />
                      </div>
                    </div>
                  )}

                  {testimonial.image && !testimonial.challengeBadge && (
                    <div className={styles.testimonialImage}>
                      <img src={testimonial.image} alt="Testimonial" />
                    </div>
                  )}
                </div>
              ))}
          </div>

          <button
            className={`${styles.carouselButton} ${styles.carouselButtonNext} ${!showNextButton ? styles.buttonHidden : ""}`}
            onClick={handleNext}
            aria-label="Next testimonial"
            disabled={!showNextButton}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>
        </div>

        <div className={styles.paginationDots}>
          {testimonials &&
            testimonials.length > cardsPerView &&
            Array.from({ length: testimonials.length - cardsPerView + 1 }).map(
              (_, index) => (
                <button
                  key={index}
                  className={`${styles.paginationDot} ${activeIndex === index ? styles.activeDot : ""}`}
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Go to testimonial set ${index + 1}`}
                />
              )
            )}
        </div>
      </div>
    </section>
  );
};

// Checkpoints Component
const Checkpoints = ({ checkpoints = [] }) => {
  const sectionRef = useRef(null);
  useScrollAnimation(sectionRef, 0.1);

  return (
    <section
      id="checkpoints"
      className={styles.checkpointsSection}
      ref={sectionRef}
    >
      <h2 className={`${styles.fadeOnScroll}`}>Checkpoints overview</h2>
      <div
        className={`${styles.checkpointsContainer} ${styles.animateOnScroll}`}
      >
        <div className={styles.checkpointsList}>
          {checkpoints &&
            checkpoints.map((checkpoint, index) => (
              <div
                key={index}
                className={`${styles.checkpointItem} ${styles.slideUpOnScroll}`}
                style={{ "--animation-order": index }}
                tabIndex={0}
                role="button"
                aria-label={`Checkpoint: ${checkpoint.checkpoint_title} on ${new Date(checkpoint.date).toLocaleDateString()}`}
              >
                <div className={styles.lockIconContainer}>
                  <div className={styles.lockIcon}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 17a2 2 0 100-4 2 2 0 000 4z" />
                      <path
                        fillRule="evenodd"
                        d="M8 5a4 4 0 018 0v2h2a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2V5zm4-1a2 2 0 00-2 2v2h4V5a2 2 0 00-2-2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <div className={styles.checkpointDetails}>
                  <div className={styles.checkpointTitle}>
                    {checkpoint.checkpoint_title}
                  </div>
                  <div className={styles.checkpointDate}>
                    {new Date(checkpoint.date).toLocaleDateString("en-US", {
                      day: "2-digit",
                      month: "short",
                    })}
                    ,{" "}
                    {new Date(checkpoint.date).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </div>
                  <div className={styles.checkpointProgressBar}>
                    <div
                      className={styles.checkpointProgress}
                      style={{
                        width: `${(index / (checkpoints.length - 1)) * 100}%`,
                      }}
                      aria-hidden="true"
                    ></div>
                  </div>
                </div>
              </div>
            ))}
        </div>
        <div
          className={`${styles.checkpointsImage} ${styles.slideLeftOnScroll}`}
        >
          <img
            src="https://d2fbpdcra4lqw8.cloudfront.net/challenge/media/writing_.png"
            alt="Checkpoints"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
};

// CallToAction Component
const CallToAction = ({
  price,
  challenge_cta_title,
  countryCode,
  onRegisterClick,
  isRegistrationDisabled,
}) => {
  const sectionRef = useRef(null);
  useScrollAnimation(sectionRef);

  return (
    <section id="cta" className={styles.ctaSection} ref={sectionRef}>
      <h2 className={`${styles.fadeOnScroll}`}>
        <div dangerouslySetInnerHTML={{ __html: challenge_cta_title }} />
      </h2>
      <div className={styles.ctaWrapper}>
        <button
          className={`${styles.registerButton} ${styles.slideUpOnScroll} ${styles.pulseAnimation} ${isRegistrationDisabled ? styles.disabledButton : ""}`}
          onClick={onRegisterClick}
          aria-label={`Register now at ${countryCode === "in" ? "₹" : "$"}${price}`}
          disabled={isRegistrationDisabled}
        >
          <span className={styles.registerButtonText}>
            {isRegistrationDisabled
              ? "Registration closed"
              : `Register now at ${countryCode === "in" ? "₹" : "$"}${price}`}
          </span>
          <span className={styles.registerButtonIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              width="24"
              height="24"
              aria-hidden="true"
            >
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </span>
        </button>
        <div className={styles.satisfactionGuarantee}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            width="20"
            height="20"
            aria-hidden="true"
          >
            <path d="M12 2L14.2 8.2H21L15.4 12.4L17.6 18.5L12 14.3L6.4 18.5L8.6 12.4L3 8.2H9.8L12 2Z" />
          </svg>
          <span>100% satisfaction guarantee</span>
        </div>
      </div>
    </section>
  );
};

// FAQ Component
const FAQ = ({ faqs = [] }) => {
  const sectionRef = useRef(null);
  const [openFAQ, setOpenFAQ] = useState(null);

  useScrollAnimation(sectionRef, 0.1);

  const toggleFAQ = (id, e) => {
    // Stop propagation to prevent interference with animations
    e.stopPropagation();
    setOpenFAQ(openFAQ === id ? null : id);
  };

  return (
    <section id="faq" className={styles.faqSection} ref={sectionRef}>
      <h2 className={`${styles.fadeOnScroll}`}>Frequently Asked Questions</h2>
      <p className={`${styles.faqSubtitle} ${styles.fadeOnScroll}`}>
        Got questions? We've got answers—click to expand 👇
      </p>
      <div
        className={`${styles.faqList} ${styles.slideUpOnScroll}`}
        role="list"
      >
        {faqs &&
          faqs.map((faq, index) => {
            // Create a consistent ID for each FAQ item
            const faqId = faq.id || `faq-${index}`;
            const isOpen = openFAQ === faqId;

            return (
              <div
                key={faqId}
                className={`${styles.faqItem} ${isOpen ? styles.active : ""}`}
                style={{ "--animation-order": index }}
                role="listitem"
              >
                <div
                  className={styles.faqQuestion}
                  onClick={(e) => toggleFAQ(faqId, e)}
                  role="button"
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${faqId}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      toggleFAQ(faqId, e);
                    }
                  }}
                >
                  <h3>{faq.question || `Question ${index + 1}`}</h3>
                  <span className={styles.faqIcon} aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </div>
                <div
                  id={`faq-answer-${faqId}`}
                  className={`${styles.faqAnswer} ${isOpen ? styles.expanded : ""}`}
                  aria-hidden={!isOpen}
                >
                  <div
                    className={styles.faqAnswerContent}
                    dangerouslySetInnerHTML={{ __html: faq.answer || "" }}
                  />
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
};

// InviteFriend Component
const InviteFriend = ({
  challenge,
  start_date,
  end_date,
  price,
  countryCode,
}) => {
  const sectionRef = useRef(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  useScrollAnimation(sectionRef);

  const handleOpenShareDialog = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "2-digit",
    });
  };

  const startDate = start_date ? formatDate(start_date) : "";
  const endDate = end_date ? formatDate(end_date) : "";

  const shareData = {
    title: challenge?.title || "21 Day Journaling Challenge",
    image:
      challenge?.hero_image ||
      require("../../Assets/invite/invite_friends_image.png"),
    startDate: startDate,
    endDate: endDate,
    price: countryCode === "in" ? "₹" + price : "$" + price,
    challengeLink:
      "https://www.myburgo.com/challenges/" +
      challenge?.slug +
      "?utm_source=challenge_page&utm_medium=invite_friend&utm_campaign=challenge_invite",
  };

  return (
    <div className={styles.inviteSection} ref={sectionRef}>
      <div className={`${styles.inviteCard} ${styles.fadeOnScroll}`}>
        <div className={styles.inviteContent}>
          <div className={styles.inviteText}>
            <h2>Challenges are better with friends!</h2>
            <p>
              Doing this with someone boosts your chances of finishing by 85%
            </p>
            <button
              className={styles.inviteButton}
              onClick={handleOpenShareDialog}
            >
              <span>Invite a friend</span>
            </button>
          </div>
          <div className={styles.inviteImage}>
            <img
              src={
                challenge?.media?.find(
                  (m) => m.image_section === "challenge_thumbnail"
                )?.media ||
                require("../../Assets/invite/invite_friends_image.png")
              }
              alt="Invite Friends"
            />
          </div>
        </div>
      </div>

      {/* Share Challenge Dialog */}
      <ShareChallengeDialog
        open={shareDialogOpen}
        onClose={handleCloseShareDialog}
        challenge={shareData}
      />
    </div>
  );
};

// Breathing Loading Component
const BreathingLoader = () => {
  const [breathingPhase, setBreathingPhase] = useState("inhale"); // "inhale" or "exhale"
  const [countdown, setCountdown] = useState(8);
  const [instructionText, setInstructionText] = useState(
    "Breathe in slowly..."
  );

  useEffect(() => {
    const breathingCycle = () => {
      // Inhale phase (4 seconds)
      setBreathingPhase("inhale");
      setInstructionText("Breathe in slowly...");

      setTimeout(() => {
        // Hold phase (1 second)
        setInstructionText("Hold...");
      }, 3500);

      setTimeout(() => {
        // Exhale phase (4 seconds)
        setBreathingPhase("exhale");
        setInstructionText("Breathe out gently...");
      }, 4500);

      setTimeout(() => {
        setInstructionText("And relax...");
      }, 7500);
    };

    // Start the breathing cycle
    breathingCycle();

    // Set up the breathing cycle interval (8 seconds total)
    const interval = setInterval(breathingCycle, 8000);

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(countdownInterval);
    };
  }, []);

  return (
    <div className={styles.breathingLoader}>
      <div className={styles.breathingBackground}>
        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={styles.floatingParticle}
            style={{
              left: `${20 + i * 10}%`,
              animationDelay: `${i * 0.5}s`,
            }}
          />
        ))}
      </div>

      <div className={styles.breathingContent}>
        <div className={styles.breathingCircleContainer}>
          <div
            className={`${styles.breathingCircle} ${
              breathingPhase === "inhale" ? styles.inhale : styles.exhale
            }`}
          >
            <div className={styles.innerCircle}>
              <div className={styles.coreCircle} />
            </div>
          </div>

          {/* Ripple effects */}
          <div className={styles.ripple} />
          <div className={styles.ripple} style={{ animationDelay: "0.5s" }} />
          <div className={styles.ripple} style={{ animationDelay: "1s" }} />
        </div>

        <div className={styles.breathingText}>
          <h2 className={styles.breathingTitle}>Take a moment to breathe</h2>
          <p className={styles.breathingInstruction}>{instructionText}</p>
          <div className={styles.breathingCountdown}>
            {countdown > 0 && (
              <span className={styles.countdownNumber}>{countdown}</span>
            )}
          </div>
        </div>

        <div className={styles.loadingProgress}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${((8 - countdown) / 8) * 100}%` }}
            />
          </div>
          <p className={styles.progressText}>Preparing your experience...</p>
        </div>
      </div>
    </div>
  );
};

// Skeleton Loading Component
const SkeletonLoader = () => {
  return (
    <div className={styles.skeletonContainer}>
      <div className={styles.skeletonHero}>
        <div className={styles.skeletonTitle}></div>
        <div className={styles.skeletonText}></div>
        <div className={styles.skeletonButton}></div>
      </div>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonSectionTitle}></div>
        <div className={styles.skeletonContent}></div>
      </div>
      <div className={styles.skeletonSection}>
        <div className={styles.skeletonSectionTitle}></div>
        <div className={styles.skeletonGrid}></div>
      </div>
    </div>
  );
};

// Main Challenge Details Component
const ChallengeDetails = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const mainRef = useRef(null);
  const { challengeSlug } = useParams();
  const navigate = useNavigate();
  const [countryCode, setCountryCode] = useState("in");
  const [activeSection, setActiveSection] = useState("hero");
  const [showMobileRegister, setShowMobileRegister] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isRegistrationDisabled, setIsRegistrationDisabled] = useState(false);

  // Define sections for navigation
  const sections = [
    { id: "hero", label: "Overview" },
    { id: "about", label: "About" },
    { id: "benefits", label: "Benefits" },
    { id: "testimonials", label: "Testimonials" },
    { id: "faq", label: "FAQ" },
  ];

  // Scroll to section function
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
    setActiveSection(sectionId);
  };

  // Check if registration should be disabled
  const checkRegistrationStatus = (startDate) => {
    if (!startDate) return false;

    const currentDate = new Date();
    const challengeStartDate = new Date(startDate);

    return currentDate > challengeStartDate;
  };

  // Handle registration click
  const handleRegisterClick = () => {
    if (isRegistrationDisabled) return;
    setIsPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setIsPaymentDialogOpen(false);
  };

  const getChallengeDetails = async () => {
    try {
      const response = await open_api_with_cookies.get(
        `/challenge/homepage/details/${challengeSlug}/`
      );
      if (response.status === 200) {
        setData(response.data);
        // Check if registration should be disabled based on start date
        if (
          response.data.challenge_batch &&
          response.data.challenge_batch.start_date
        ) {
          setIsRegistrationDisabled(
            checkRegistrationStatus(response.data.challenge_batch.start_date)
          );
        }
      } else if (response.status === 404) {
        return <PageNotFound />;
      }
    } catch (error) {
      console.error("Error fetching challenge details:", error);
    }
  };

  const fetchCountry = async () => {
    const countryCode = await getCountryCode();
    setCountryCode(countryCode);
  };

  useEffect(() => {
    // ====== LOADING CONFIGURATION ======
    // Choose your loader type by commenting/uncommenting:
    // const USE_BREATHING_LOADER = true; // Set to true for breathing loader
    const USE_BREATHING_LOADER = false; // Set to false for skeleton loader

    // Set loading timeout based on loader type
    const LOADING_TIMEOUT = USE_BREATHING_LOADER ? 8000 : 1500; // 8s for breathing, 1.5s for skeleton

    // Load data function
    const loadData = async () => {
      await fetchCountry();
      await getChallengeDetails();
    };

    const timer = setTimeout(() => {
      loadData().then(() => {
        setLoading(false);
      });
    }, LOADING_TIMEOUT);

    // Initialize the global scroll animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    // Target all animation classes throughout the page
    const animatedElements = document.querySelectorAll(
      `.${styles.fadeOnScroll}, .${styles.slideUpOnScroll}, .${styles.slideLeftOnScroll}, .${styles.slideRightOnScroll}, .${styles.animateOnScroll}`
    );

    animatedElements.forEach((el) => {
      observer.observe(el);
    });

    // Set up scroll listener for active section detection
    const handleScroll = () => {
      // Show/hide mobile sticky register button based on scroll position
      if (window.scrollY > 300) {
        setShowMobileRegister(true);
      } else {
        setShowMobileRegister(false);
      }

      // Find the current section in view
      const scrollPosition = window.scrollY + 100; // Add offset to improve detection

      // Get all section elements
      const sectionElements = sections.map((section) => ({
        id: section.id,
        offsetTop: document.getElementById(section.id)?.offsetTop || 0,
      }));

      // Sort by proximity to current scroll position
      sectionElements.sort((a, b) => {
        return (
          Math.abs(scrollPosition - a.offsetTop) -
          Math.abs(scrollPosition - b.offsetTop)
        );
      });

      // Set the closest section as active
      if (sectionElements.length > 0) {
        setActiveSection(sectionElements[0].id);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      clearTimeout(timer);
      animatedElements.forEach((el) => {
        observer.unobserve(el);
      });
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // ====== LOADING DISPLAY LOGIC ======
  if (loading || !data) {
    // ===== CHOOSE YOUR LOADER =====
    // Comment/uncomment the loader you want to use:

    // Option 1: Breathing Loader (8-second meditation experience)
    // return <BreathingLoader />;

    // Option 2: Skeleton Loader (Quick loading animation)
    return <SkeletonLoader />;
  }

  // Create a safe data object with defaults
  const safeData = {
    ...data,
    benefits: data.benefits || [],
    testimonials: data.testimonials || [],
    checkpoints: data.limited_checkpoints || [],
    faqs: data.faqs || [],
    start_date: data.challenge_batch.start_date || "",
    end_date: data.challenge_batch.end_date || "",
    challenge_price:
      countryCode === "in"
        ? parseInt(data.selling_price)
        : parseInt(data.selling_price_usd),
    media: data.media || [],
    challenge_cta_title: data.challenge_cta_title || "",
  };

  return (
    <div className={styles.challengeDetailsContainer} ref={mainRef}>
      {/* Sticky section navigator */}
      {/* <SectionNavigator
        sections={sections}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
      /> */}

      {/* Mobile sticky register button */}
      {showMobileRegister && (
        <div className={styles.mobileRegisterSticky}>
          <button
            className={`${styles.registerButton} ${isRegistrationDisabled ? styles.disabledButton : ""}`}
            onClick={handleRegisterClick}
            aria-label="Register now"
            disabled={isRegistrationDisabled}
          >
            <span className={styles.registerButtonText}>
              {isRegistrationDisabled
                ? "Registration closed"
                : `Register now at ${countryCode === "in" ? "₹" : "$"}${safeData.challenge_price}`}
            </span>
          </button>
        </div>
      )}

      <HeroSection
        data={safeData}
        countryCode={countryCode}
        onRegisterClick={handleRegisterClick}
        price={safeData.challenge_price}
        isRegistrationDisabled={isRegistrationDisabled}
      />
      <AboutChallenge data={safeData} />
      <Benefits benefits={safeData.benefits} />
      <DateRange
        start_date={safeData.start_date}
        end_date={safeData.end_date}
      />
      <Testimonials testimonials={safeData.testimonials} />
      <CallToAction
        price={safeData.challenge_price}
        challenge_cta_title={safeData.challenge_cta_title}
        countryCode={countryCode}
        onRegisterClick={handleRegisterClick}
        isRegistrationDisabled={isRegistrationDisabled}
      />
      <Checkpoints checkpoints={safeData.checkpoints} />
      <FAQ faqs={safeData.faqs} />
      <InviteFriend
        challenge={safeData}
        start_date={safeData.start_date}
        end_date={safeData.end_date}
        price={safeData.challenge_price}
        countryCode={countryCode}
      />

      {/* Conditionally render the Payment Dialog */}
      {isPaymentDialogOpen && (
        <ChallengePaymentPage
          open={isPaymentDialogOpen}
          onClose={handleClosePaymentDialog}
          challengeSlugFromProp={challengeSlug}
        />
      )}
    </div>
  );
};

export default ChallengeDetails;
